// Firebase configuration for production
// Works with Vercel SSR and client-side

import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  connectAuthEmulator, 
  setPersistence, 
  browserLocalPersistence, 
  browserSessionPersistence,
  inMemoryPersistence,
  Auth,
  Persistence 
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, enableIndexedDbPersistence, Firestore, FirestoreSettings, CACHE_SIZE_UNLIMITED } from 'firebase/firestore';
import { getStorage, connectStorageEmulator, FirebaseStorage } from 'firebase/storage';
import { getAnalytics, isSupported, Analytics } from 'firebase/analytics';
import { getFunctions, httpsCallable, Functions, connectFunctionsEmulator } from 'firebase/functions';
import { initAppCheck } from './appCheck';

// Firebase config - from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "YOUR_API_KEY",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "your-project.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "your-project",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "your-project.appspot.com",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "123456789",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:123456789:web:abc123",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "G-XXXXXXXXXX"
};

// Determine environment type
const getEnvType = (): 'development' | 'production' => {
  // Check VITE_ENV_TYPE first (custom env var)
  if (import.meta.env.VITE_ENV_TYPE) {
    return import.meta.env.VITE_ENV_TYPE as 'development' | 'production';
  }
  // Fall back to VERCEL_ENV or import.meta.env.PROD
  if (import.meta.env.VERCEL_ENV === 'production') {
    return 'production';
  }
  return import.meta.env.PROD ? 'production' : 'development';
};

// Get persistence type based on environment and settings
const getAuthPersistence = (): Persistence => {
  // Check explicit setting first
  if (import.meta.env.VITE_AUTH_PERSISTENCE) {
    switch (import.meta.env.VITE_AUTH_PERSISTENCE) {
      case 'local':
        return browserLocalPersistence;
      case 'session':
        return browserSessionPersistence;
      case 'none':
        return inMemoryPersistence;
    }
  }
  
  // In development with emulators, use session or none
  if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
    // Use session persistence for emulator to avoid conflicts
    return browserSessionPersistence;
  }
  
  // Default: local persistence (survives browser close and cache clear)
  return browserLocalPersistence;
};

// Log environment info
const logEnvInfo = () => {
  const envType = getEnvType();
  console.log(`[Firebase] Environment: ${envType}`);
  console.log(`[Firebase] Auth persistence: ${getAuthPersistence().type}`);
  
  if (import.meta.env.DEV) {
    console.log(`[Firebase] Using emulators: ${import.meta.env.VITE_USE_EMULATORS === 'true'}`);
  }
};

// Firebase instances - initialized lazily in browser only
let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;
let analytics: Analytics | null = null;
let functions: Functions;
let initialized = false;

// Initialize Firebase - only in browser
const initializeFirebase = () => {
  if (initialized || typeof window === 'undefined') return;
  
  // Prevent multiple initializations
  if (getApps().length === 0) {
    app = initializeApp(firebaseConfig);
  } else {
    app = getApps()[0];
  }
  
  // Initialize services
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
  
  // Use emulator with CORS proxy in development mode
  const useEmulators = import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true';
  console.log('[Firebase] DEV:', import.meta.env.DEV, 'VITE_USE_EMULATORS:', import.meta.env.VITE_USE_EMULATORS, 'Result:', useEmulators);
  
  if (useEmulators) {
    console.log('[Firebase] Connecting to emulators via CORS proxy (port 5002)...');
    
    // Подключаем Functions через прокси на порту 5002
    // Прокси добавляет заголовки CORS которых нет в эмуляторе
    functions = getFunctions(app, 'europe-central2');
    connectFunctionsEmulator(functions, 'localhost', 5002);
    
    // Остальные сервисы подключаем напрямую (они работают через Firebase SDK)
    connectAuthEmulator(auth, "http://localhost:9099");
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectStorageEmulator(storage, 'localhost', 9199);
    console.log('[Firebase] Emulators connected via CORS proxy (port 5002)');
  } else {
    functions = getFunctions(app, 'europe-central2');
    console.log('[Firebase] Using production Firebase services');
  }
  
  // Initialize App Check
  initAppCheck(app);
  
  initialized = true;
  
  // Initialize Analytics (async, only in browser)
  // В режиме эмуляторов Analytics не работает - пропускаем
  if (useEmulators) {
    console.log('[Firebase] Analytics disabled in emulator mode');
  } else {
    isSupported().then((supported) => {
      if (supported) {
        analytics = getAnalytics(app);
      }
    }).catch(() => {
      console.log('Analytics not supported in this environment');
    });
  }
  
  // Configure persistence - called before any auth operations
  let persistenceConfigured = false;
  const enablePersistence = async () => {
    if (persistenceConfigured) return;
    
    const envType = getEnvType();
    const persistence = getAuthPersistence();
    
    // Use emulators in development
    if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
      // Emulators already connected in initializeFirebase()
      // This is just for consistency check
      console.log('[Firebase] Emulators already connected');
    } else {
      // Enable Firestore persistence (only when not using emulators)
      // Using new API with cache settings for multi-tab support
      try {
        const settings: FirestoreSettings = {
          cacheSizeBytes: CACHE_SIZE_UNLIMITED
        };
        // Note: synchronizeTabs is enabled by default in Firestore SDK 9.23+
        // For older versions or to explicitly enable multi-tab sync:
        // (settings as any).synchronizeTabs = true;
        
        // Try new API first (SDK 9.23+)
        if ('cache' in db) {
          // @ts-ignore - cache property exists in newer SDK versions
          await db.cache().getEnabled();
          console.log('[Firebase] Firestore cache available');
        } else {
          // Fallback to deprecated API
          console.log('[Firebase] Using legacy persistence API (SDK < 9.23)');
          await enableIndexedDbPersistence(db);
        }
        console.log('[Firebase] Firestore persistence enabled');
      } catch (err: any) {
        // Always log as info, not error - persistence is optional
        console.log('[Firebase] Firestore persistence:', err.message || 'using default (no cache)');
        // Don't block - continue without persistence
      }
    }
    
    // Set auth persistence FIRST
    try {
      await setPersistence(auth, persistence);
      persistenceConfigured = true;
      console.log(`[Firebase] Auth persistence set to: ${persistence.type} (${envType} mode)`);
    } catch (err: any) {
      // This can happen if called after auth state has been modified
      console.warn('[Firebase] Auth persistence set failed:', err.message);
      console.warn('[Firebase] This is normal if user is already logged in');
    }
  };
  
  enablePersistence();
};

// Initialize on module load (only in browser)
if (typeof window !== 'undefined') {
  initializeFirebase();
}

// Export initialized services
// Note: These should only be used after checking isFirebaseAvailable()
export const getFirebaseApp = (): FirebaseApp => {
  if (typeof window === 'undefined') {
    throw new Error('Firebase can only be used in browser environment');
  }
  if (!initialized) {
    initializeFirebase();
  }
  return app!;
};

export const getFirebaseAuth = (): Auth => {
  if (typeof window === 'undefined') {
    throw new Error('Firebase Auth can only be used in browser environment');
  }
  if (!initialized) {
    initializeFirebase();
  }
  return auth!;
};

export const getFirebaseDb = (): Firestore => {
  if (typeof window === 'undefined') {
    throw new Error('Firebase Firestore can only be used in browser environment');
  }
  if (!initialized) {
    initializeFirebase();
  }
  return db!;
};

export const getFirebaseStorage = (): FirebaseStorage => {
  if (typeof window === 'undefined') {
    throw new Error('Firebase Storage can only be used in browser environment');
  }
  if (!initialized) {
    initializeFirebase();
  }
  return storage!;
};

export const getFirebaseFunctions = (): Functions => {
  if (typeof window === 'undefined') {
    throw new Error('Firebase Functions can only be used in browser environment');
  }
  if (!initialized) {
    initializeFirebase();
  }
  return functions!;
};

export { app, auth, db, storage, analytics, functions, httpsCallable };

// ============================================
// Firebase App Check Configuration
// ============================================
// ВАЖНО: App Check требует настройки reCAPTCHA в Firebase Console
// 
// Для production:
// 1. Настройте reCAPTCHA v3 в Firebase Console > App Check
// 2. Получите SITE_KEY и добавьте в VITE_RECAPTCHA_SITE_KEY
// 3. Раскомментируйте код ниже
//
// Для development/emulators:
// - Используйте Debug provider (см. src/lib/appCheck.ts)
// - Добавьте debug токен в Firebase Console > App Check
//
// Пример конфигурации (раскомментировать после настройки reCAPTCHA):
// 
// const initAppCheck = () => {
//   if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
//     // Для эмуляторов используем Debug provider
//     // Токен можно получить в консоли браузера при первом запуске
//     console.log('[AppCheck] Using Debug provider for emulators');
//   } else {
//     // Для production используем ReCaptchaV3Provider
//     const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
//     if (siteKey) {
//       initializeAppCheck(app, {
//         provider: new ReCaptchaV3Provider(siteKey),
//         isTokenAutoRefreshEnabled: true,
//       });
//       console.log('[AppCheck] Initialized with ReCaptchaV3Provider');
//     }
//   }
// };
//
// Вызов инициализации после создания app:
// initAppCheck();

// Export types
export type { User } from 'firebase/auth';
export type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

// Check if Firebase is properly configured
export const isFirebaseConfigured = () => {
  return firebaseConfig.apiKey !== "YOUR_API_KEY" && 
         firebaseConfig.projectId !== "your-project";
};

// Export environment helpers
export { getEnvType, getAuthPersistence };
